import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar, { Logo } from "@/components/Navbar";
import api, { MASCOT } from "@/lib/api";
import { Check, Camera, Sparkles, FileText, Video, Wand2, ArrowRight, Star } from "lucide-react";

const STEPS = [
  { icon: Camera, title: "Envie 6+ fotos", text: "Tire fotos do produto de vários ângulos e mande de uma vez." },
  { icon: Sparkles, title: "Solange analisa", text: "A IA lê as fotos e entende o que é o seu produto." },
  { icon: FileText, title: "Anúncio pronto", text: "Título, descrição e categoria no padrão da Shopee." },
  { icon: Video, title: "Fotos + vídeo", text: "3 imagens tratadas e um vídeo de 15s pra converter mais." },
];

function PlanCard({ plan, onChoose }) {
  return (
    <div
      data-testid={`plan-card-${plan.id}`}
      className={`relative rounded-3xl p-8 flex flex-col ${
        plan.highlight
          ? "bg-[#9c5f27] text-white shadow-2xl scale-[1.03] border-2 border-[#f0791f]"
          : "bg-white border border-[#e8d8c8] shadow-sm"
      }`}
    >
      {plan.highlight && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#f0791f] text-white text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full flex items-center gap-1">
          <Star size={13} fill="white" /> Mais popular
        </div>
      )}
      <h3 className={`font-display text-2xl font-bold ${plan.highlight ? "text-white" : "text-[#9c5f27]"}`}>{plan.name}</h3>
      <div className="mt-4 flex items-baseline gap-1">
        <span className={`text-4xl font-bold font-display ${plan.highlight ? "text-white" : "text-[#2e1e10]"}`}>
          R$ {plan.price.toFixed(2).replace(".", ",")}
        </span>
        <span className={plan.highlight ? "text-white/70" : "text-[#8c735f]"}>/{plan.period}</span>
      </div>
      <ul className="mt-6 space-y-3 flex-1">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm">
            <Check size={18} className={plan.highlight ? "text-[#f0aa6e] mt-0.5 shrink-0" : "text-[#f0791f] mt-0.5 shrink-0"} />
            <span className={plan.highlight ? "text-white/90" : "text-[#5c4634]"}>{f}</span>
          </li>
        ))}
      </ul>
      <button
        onClick={() => onChoose(plan)}
        data-testid={`choose-plan-${plan.id}`}
        className={`mt-8 w-full py-3.5 rounded-full font-semibold text-sm transition-all ${
          plan.highlight ? "bg-[#f0791f] hover:bg-[#d9640d] text-white" : "btn-orange"
        }`}
      >
        Assinar {plan.name}
      </button>
    </div>
  );
}

export default function Landing() {
  const [plans, setPlans] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/checkout/plans").then(({ data }) => setPlans(data.plans)).catch(() => {});
  }, []);

  const choose = (plan) => navigate(`/checkout?plan=${plan.id}`);

  return (
    <div className="min-h-screen mesh-warm">
      <Navbar />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 lg:pt-20 lg:pb-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-[#c17f3e] bg-[#f5ebe1] px-4 py-1.5 rounded-full">
              <Wand2 size={14} /> Sua assistente de anúncios na Shopee
            </span>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#9c5f27] leading-[1.05]">
              Cadastre na Shopee em <span className="text-[#f0791f]">minutos</span>, não em horas.
            </h1>
            <p className="mt-5 text-lg text-[#5c4634] leading-relaxed max-w-xl">
              Manda pelo menos 6 fotos do seu produto que a Solange devolve o anúncio pronto:
              título, descrição, categoria certinha, imagens tratadas e um vídeo de 15 segundos.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#planos" className="btn-orange px-7 py-3.5 rounded-full font-semibold flex items-center gap-2" data-testid="hero-cta">
                Escolher meu plano <ArrowRight size={18} />
              </a>
              <button onClick={() => navigate("/login")} className="px-7 py-3.5 rounded-full font-semibold text-[#9c5f27] border border-[#c17f3e] hover:bg-[#f5ebe1] transition-colors" data-testid="hero-login">
                Já sou assinante
              </button>
            </div>
            <div className="mt-8 flex items-center gap-4 text-sm text-[#8c735f]">
              <Check size={16} className="text-[#f0791f]" /> Título de até 120 caracteres
              <Check size={16} className="text-[#f0791f]" /> Categoria da árvore Shopee
            </div>
          </div>

          <div className="relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="absolute inset-0 bg-[#f0791f]/10 blur-3xl rounded-full" />
            <div className="relative brand-gradient rounded-[2.5rem] p-8 border border-[#e8d8c8] shadow-xl">
              <img src={MASCOT.happy} alt="Solange, a Postadora" className="animate-float-slow w-full max-w-md mx-auto drop-shadow-2xl" />
              <div className="absolute bottom-8 left-8 bg-white rounded-2xl shadow-lg px-4 py-3 border border-[#e8d8c8] max-w-[220px]">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-[#c17f3e]">Solange</div>
                <p className="text-sm text-[#2e1e10]">"Deixa a parte chata comigo, patrão!" 🐾</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Como funciona */}
      <section className="bg-white border-y border-[#e8d8c8] py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs font-semibold uppercase tracking-[0.15em] text-[#c17f3e]">Como funciona</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-[#9c5f27]">Do celular pra Shopee em 4 passos</h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <div key={s.title} className="rounded-2xl border border-[#e8d8c8] bg-[#fdf8f3] p-6 hover:shadow-md transition-all" data-testid={`step-${i}`}>
                <div className="w-12 h-12 rounded-xl bg-[#f0791f] flex items-center justify-center text-white">
                  <s.icon size={22} />
                </div>
                <div className="mt-4 text-xs font-bold text-[#c17f3e]">PASSO {i + 1}</div>
                <h3 className="mt-1 font-display text-lg font-semibold text-[#2e1e10]">{s.title}</h3>
                <p className="mt-2 text-sm text-[#5c4634] leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Planos */}
      <section id="planos" className="py-16 lg:py-24 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs font-semibold uppercase tracking-[0.15em] text-[#c17f3e]">Planos</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-[#9c5f27]">Escolha e comece hoje</h2>
            <p className="mt-3 text-[#5c4634]">O acesso libera assim que o pagamento é confirmado. Cancele quando quiser.</p>
          </div>
          <div className="mt-14 grid md:grid-cols-3 gap-6 lg:gap-8 items-start" data-testid="subscription-plans-view">
            {plans.map((p) => <PlanCard key={p.id} plan={p} onChoose={choose} />)}
          </div>
        </div>
      </section>

      <footer className="border-t border-[#e8d8c8] bg-white py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Logo />
          <p className="text-sm text-[#8c735f]">Feito com carinho pela Solange 🐾 — não afiliada à Shopee.</p>
        </div>
      </footer>
    </div>
  );
}
